import React, { createContext, useState, useContext, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { loginUser, logoutUser, registerUser, signInWithGoogle, getUserData, deleteUserAccount } from '../firebase/services';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userData = await getUserData(firebaseUser.uid);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || userData?.name || 'User',
            role: userData?.role || 'user',
            provider: userData?.provider || (firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email')
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || 'User',
            role: 'user',
            provider: firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email'
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      await loginUser(email, password);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      alert(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      setLoading(true);
      await registerUser(email, password, name);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      alert(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
      return true;
    } catch (error) {
      console.error('Google login error:', error);
      alert(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    try {
      setLoading(true);
      await deleteUserAccount();
      setUser(null);
      return true;
    } catch (error) {
      console.error('Delete account error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    register,
    logout,
    deleteAccount,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
